import { useState } from "react";
import { useI18n } from "../i18n";
import { importEmployeesV2 } from "../data";
import { parseCsv } from "../lib/csv";
import { downloadBlob, readXlsx, writeXlsx } from "../lib/xlsx";
import type { ImportResult, Workspace } from "../types";

/** Template columns: key, required, guide (English), guide (Arabic), example. */
const COLUMNS: [string, boolean, string, string, string][] = [
  ["first_name", true, "First name (English or Arabic)", "الاسم الأول", "Mona"],
  ["last_name", true, "Last name", "اسم العائلة", "Adel"],
  ["first_name_ar", false, "First name in Arabic", "الاسم الأول بالعربية", "منى"],
  ["last_name_ar", false, "Last name in Arabic", "اسم العائلة بالعربية", "عادل"],
  ["gender", false, "male / female (or ذكر / أنثى)", "ذكر / أنثى", "female"],
  ["date_of_birth", false, "Date (YYYY-MM-DD, DD/MM/YYYY or an Excel date)", "تاريخ", "1994-05-12"],
  ["nationality", false, "Two-letter country code; empty = EG", "رمز الدولة من حرفين؛ فارغ = مصر", "EG"],
  ["national_id", false, "14 digits for Egyptians; must be unique", "14 رقمًا للمصريين؛ لا يتكرر", "29405120101234"],
  ["passport_number", false, "For non-Egyptians", "لغير المصريين", ""],
  ["mobile", false, "e.g. 01001234567", "مثال 01001234567", "01001234567"],
  ["personal_email", false, "", "", "mona@example.com"],
  ["work_email", false, "", "", ""],
  ["employee_number", false, "Your staff number; needed if others report to this person", "الرقم الوظيفي؛ مطلوب إذا كان مديرًا لآخرين", "1001"],
  ["hire_date", true, "First working day", "تاريخ التعيين", "2022-11-01"],
  ["job_title", false, "", "", "Accountant"],
  ["job_title_ar", false, "", "", "محاسب"],
  ["department", false, "Must already exist under Organisation", "يجب أن يكون موجودًا في الهيكل التنظيمي", "Finance"],
  ["branch", false, "Empty = head office", "فارغ = المقر الرئيسي", ""],
  ["grade", false, "", "", "B2"],
  ["fte", false, "1 = full time, 0.5 = half time", "1 = دوام كامل", "1"],
  ["contract_type", false, "permanent, fixed_term, … (or the name)", "نوع العقد", "permanent"],
  ["contract_end_date", false, "For fixed-term contracts", "للعقود محددة المدة", ""],
  ["probation_end_date", false, "", "نهاية فترة الاختبار", ""],
  ["manager_employee_number", false, "The manager's employee_number (in this file or already in PeopleCore)", "الرقم الوظيفي للمدير", "1000"],
  ["basic_salary", false, "Monthly, EGP", "شهريًا بالجنيه", "12000"],
  ["housing_allowance", false, "Monthly, EGP", "شهريًا", ""],
  ["transport_allowance", false, "Monthly, EGP", "شهريًا", "800"],
  ["meal_allowance", false, "Monthly, EGP", "شهريًا", ""],
  ["mobile_allowance", false, "Monthly, EGP", "شهريًا", ""],
  ["other_allowance", false, "Monthly, EGP", "شهريًا", ""],
  ["insured", false, "yes / no (social insurance); empty = yes", "مؤمن عليه: نعم / لا", "yes"],
  ["insurance_number", false, "Social insurance number", "الرقم التأميني", "1234567"],
  ["insurance_salary", false, "Insured wage, if different", "أجر الاشتراك", "9000"],
  ["payment_method", false, "bank / cash / wallet", "بنك / نقدي / محفظة", "bank"],
  ["bank_name", false, "", "", "CIB"],
  ["bank_account", false, "Account number or IBAN", "رقم الحساب أو IBAN", "EG380019000500000000263180002"],
  ["annual_leave_balance", false, "Annual leave days left today (from your old records)", "رصيد الإجازة السنوية المتبقي اليوم", "9.5"],
];
const KEYS = COLUMNS.map((c) => c[0]);
const TEXT_COLS = ["national_id", "passport_number", "mobile", "employee_number", "manager_employee_number", "insurance_number", "bank_account"].map((k) => KEYS.indexOf(k));
const DATE_KEYS = new Set(["date_of_birth", "hire_date", "contract_end_date", "probation_end_date"]);

const pad = (n: number) => String(n).padStart(2, "0");
/** Excel serial numbers and DD/MM/YYYY become YYYY-MM-DD; anything else is left for the server to judge. */
function normDate(v: string): string {
  if (/^\d{4,5}(\.\d+)?$/.test(v)) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(v)) * 86400000);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  }
  const m = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) return `${m[3]}-${pad(Number(m[2]))}-${pad(Number(m[1]))}`;
  const iso = v.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})/);
  if (iso) return `${iso[1]}-${pad(Number(iso[2]))}-${pad(Number(iso[3]))}`;
  return v;
}
const digits = (v: string) => v.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

function toRecords(table: string[][]): Record<string, string>[] {
  const nonEmpty = table.filter((r) => r.some((v) => (v ?? "").trim() !== ""));
  const [header, ...body] = nonEmpty;
  if (!header) return [];
  const keys = header.map((h) => (h ?? "").trim().toLowerCase().replace(/\*/g, "").trim().replace(/\s+/g, "_"));
  return body.map((r) => {
    const o: Record<string, string> = {};
    keys.forEach((k, i) => {
      if (!k) return;
      let v = digits((r[i] ?? "").trim());
      if (DATE_KEYS.has(k) && v) v = normDate(v);
      if (k === "mobile" && /^1\d{9}$/.test(v)) v = `0${v}`;
      o[k] = v;
    });
    return o;
  });
}

function template() {
  return writeXlsx([
    { name: "Employees", rows: [KEYS], widths: KEYS.map((k) => Math.max(12, k.length + 2)), textColumns: TEXT_COLS },
    {
      name: "Guide",
      rows: [["Column", "Required", "What to enter", "الوصف", "Example"], ...COLUMNS.map(([k, req, en, ar, ex]) => [k, req ? "yes" : "", en, ar, ex])],
      widths: [24, 9, 60, 40, 30],
    },
  ]);
}

type Step = "pick" | "checked" | "done";

export function ImportEmployees({ ws, onDone }: { ws: Workspace; onDone: () => void }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const [rows, setRows] = useState<Record<string, string>[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>("pick");
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [unknown, setUnknown] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setResults(null); setError(null); setStep("pick"); setFileName(file.name); setRows(null);
    setBusy(true);
    try {
      let table: string[][];
      if (/\.xlsx$/i.test(file.name)) table = await readXlsx(file);
      else if (/\.xls$/i.test(file.name)) throw new Error(t("impOldXls"));
      else { const csv = parseCsv(await file.text()); table = csv.length ? [Object.keys(csv[0]), ...csv.map((r) => Object.values(r))] : []; }
      const recs = toRecords(table);
      if (recs.length === 0) throw new Error(t("impEmpty"));
      if (recs.length > 2000) throw new Error(t("impTooMany"));
      setUnknown(Object.keys(recs[0]).filter((k) => !KEYS.includes(k)));
      setRows(recs);
      setResults(await importEmployeesV2(cid, recs, true));
      setStep("checked");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  async function run() {
    if (!rows) return;
    setBusy(true); setError(null);
    try { setResults(await importEmployeesV2(cid, rows, false)); setStep("done"); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
    finally { setBusy(false); }
  }

  const ok = results?.filter((r) => r.ok) ?? [];
  const bad = results?.filter((r) => !r.ok) ?? [];
  const warned = ok.filter((r) => r.warnings && r.warnings.length > 0);
  const nameOf = (n: number) => { const r = rows?.[n - 1]; return r ? `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() : ""; };
  function downloadProblems() {
    if (!rows) return;
    const keys = Object.keys(rows[0]);
    const list = bad.map((r) => [...keys.map((k) => rows[r.row - 1]?.[k] ?? ""), r.error ?? ""]);
    downloadBlob("peoplecore-import-problems.xlsx", writeXlsx([{ name: "Employees", rows: [[...keys, "error"], ...list], widths: [...keys.map(() => 14), 60] }]));
  }
  const show = [...bad, ...warned].sort((a, b) => a.row - b.row);

  return (
    <div className="page">
      <button type="button" className="link back" onClick={onDone}>← {t("backToList")}</button>
      <section className="card form-card">
        <h2>{t("importTitle")}</h2>
        <ol className="steps">
          <li>
            <p>{t("impStep1")}</p>
            <button type="button" className="btn secondary" onClick={() => downloadBlob("peoplecore-employees.xlsx", template())}>{t("impTemplate")}</button>
          </li>
          <li>
            <p>{t("impStep2")}</p>
            <label className="btn secondary file-btn">
              {t("impChoose")}
              <input id="import-file" type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {busy && step === "pick" && <p className="muted">{t("impChecking")}</p>}
            {rows && <p className="muted">{fileName}: <strong>{rows.length}</strong> {t("rowsFound")}</p>}
            {unknown.length > 0 && <p className="alert warning">{t("impUnknownCols")}: {unknown.join(", ")}</p>}
          </li>
        </ol>
        {error && <p className="alert danger" role="alert">{error}</p>}
        {results && (
          <div className="stack">
            <div className="row">
              <span className="badge success">{ok.length} {step === "done" ? t("imported") : t("impReady")}</span>
              {warned.length > 0 && <span className="badge warning">{warned.length} {t("impWithNotes")}</span>}
              {bad.length > 0 && <span className="badge danger">{bad.length} {step === "done" ? t("failed") : t("impWillFail")}</span>}
            </div>
            {step === "checked" && <p className="muted small-note">{bad.length ? t("impFixHint") : t("impAllGood")}</p>}
            {step === "done" && <p className="alert info">{t("impDone")}</p>}
            {show.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>{t("row")}</th><th>{t("fullName")}</th><th>{t("impProblem")}</th></tr></thead>
                  <tbody>
                    {show.map((r) => (
                      <tr key={r.row}>
                        <td className="mono">{r.row + 1}</td>
                        <td>{nameOf(r.row)}</td>
                        <td className={r.ok ? "" : "danger-text"}>{r.ok ? r.warnings!.join(" · ") : r.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {bad.length > 0 && <div className="actions start"><button type="button" className="btn secondary small" onClick={downloadProblems}>{t("impDownloadProblems")}</button></div>}
          </div>
        )}
        <div className="actions">
          <button type="button" className="btn secondary" onClick={onDone}>{t("close")}</button>
          {step === "checked" && ok.length > 0 && (
            <button type="button" className="btn primary" disabled={busy} onClick={run}>
              {busy ? t("importing") : t("impRun").replace("{n}", String(ok.length))}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
